import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon } from "@phosphor-icons/react";

/**
 * DarkModeToggle Component
 * Author: MCGdev
 * Memoized for better performance
 * Updated to match mobile menu button styling
 */

const DarkModeToggle = React.memo(({ darkMode, toggleDarkMode }) => {
  return (
    <motion.button
      onClick={toggleDarkMode}
      aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
      aria-pressed={darkMode}
      className={`relative p-2.5 sm:p-3 lg:px-6 lg:py-3 rounded-xl border transition-all duration-300 overflow-hidden group backdrop-blur-md transform hover:scale-105 ${
        darkMode
          ? "bg-gradient-to-br from-slate-800/50 to-slate-700/50 border-slate-600/50 text-yellow-400"
          : "bg-gradient-to-br from-white/30 to-white/20 border-white/30 text-blue-900"
      }`}
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.95 }}
      style={{
        background: darkMode 
          ? "linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(51, 65, 85, 0.6) 100%)"
          : "linear-gradient(135deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.2) 100%)",
        boxShadow: darkMode
          ? "inset 0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
          : "inset 0 2px 4px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
      }}
    >
      {/* Neon orange hover effect overlay - Light Mode ONLY */}
      {!darkMode && (
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/0 via-orange-400/0 to-orange-500/0 group-hover:from-orange-500/40 group-hover:via-orange-400/60 group-hover:to-orange-500/40 transition-all duration-500 rounded-xl" />
      )}
      
      {/* Animated border glow on hover */}
      <div className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute inset-0 rounded-xl border-2 border-orange-400/80 animate-pulse" />
      </div>
      
      <span className="relative z-10">
        {darkMode ? 
          <Sun size={24} weight="fill" className="text-yellow-400" /> : 
          <Moon size={24} weight="fill" className="text-blue-900" />
        }
      </span>
    </motion.button>
  );
});

DarkModeToggle.displayName = 'DarkModeToggle';

export default DarkModeToggle;
